import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from "react";

import { Comment } from "@/api";

import { CommentsProps } from "../Comments";
import { useComments } from "../hooks/useComments";
import { useUser } from "@/providers/context";
import { useUserDetails } from "@/hooks";

interface ContextValue extends CommentsProps {
  sortedComments: Comment[] | undefined;
  pendingComment: Comment | null;
  setPendingComment: Dispatch<
    SetStateAction<
      | (Comment & {
          ParentCommentId?: number;
        })
      | null
    >
  >;
  labelWithCount: string;
  defaultPendingComment: Pick<
    Comment,
    "SourceCommentId" | "Name" | "Thumbnail" | "SourceCustomerId" | "Responses"
  >;
  userThumbnail: string;
  commentToEdit: Comment | undefined;
  setCommentToEdit: Dispatch<SetStateAction<Comment | undefined>>;
}

interface ProviderProps {
  props: CommentsProps;
  children: ReactNode | ReactNode[];
}

type OptionalContextValue = ContextValue | undefined;
const CommentsContext = createContext<OptionalContextValue>(undefined);

const CommentsProvider = ({ props, children }: ProviderProps) => {
  const { sortedComments, pendingComment, setPendingComment, labelWithCount } =
    useComments(props);
  const [commentToEdit, setCommentToEdit] = useState<Comment>();

  const { user_name, uid } = useUser();
  const { data: userDetails } = useUserDetails();
  const thumbnailSource = userDetails?.avatar_url ?? "";

  const defaultPendingComment = useMemo(() => {
    return {
      SourceCommentId: 0,
      Name: user_name ?? "",
      Thumbnail: thumbnailSource,
      SourceCustomerId: uid,
      Responses: [],
    };
  }, [user_name, uid, thumbnailSource]);

  return (
    <CommentsContext.Provider
      value={{
        ...props,
        sortedComments,
        pendingComment,
        setPendingComment,
        labelWithCount,
        defaultPendingComment,
        userThumbnail: thumbnailSource,
        commentToEdit,
        setCommentToEdit,
      }}
    >
      {children}
    </CommentsContext.Provider>
  );
};

export { CommentsContext, CommentsProvider };
